// Single source of truth for the shape of every Sentry event we send.
// Runtime-specific wrappers (browser/node/nextjs) inject their own captureException and call buildSentryEnvelope.

export const ERROR_DOMAINS = [
	"integration",
	"thread",
	"message",
	"webhook",
	"api-v2",
	"zero",
	"live-chat",
	"ai-chat",
	"linear",
	"billing",
	"auth",
	"mail",
	"slack",
	"teams",
	"intercom",
	"hubspot",
	"portal",
	"general",
] as const

export type ErrorDomain = (typeof ERROR_DOMAINS)[number]

// The deployable that emitted the error, independent of the feature domain.
export const SERVICES = ["web", "live", "atlas", "app", "api"] as const

export type ServiceName = (typeof SERVICES)[number]

export interface ErrorContext {
	/** High-level area the error happened in. Becomes part of the title and a searchable tag. */
	domain: ErrorDomain
	/** Short verb phrase for the specific action, e.g. "create issue", "send message", "deliver webhook". */
	action: string
	/** Always include when known. */
	workspaceId?: string | null
	/** Always include when known. */
	userId?: string | null
	/** Any extra structured data. Keep keys snake/camel consistent within a domain; values must be serializable. */
	extra?: Record<string, unknown>
}

export interface SentryEnvelope {
	/** Stable, scannable title: "[domain] action: <error message>". */
	error: Error
	captureContext: {
		tags: Record<string, string>
		extra: Record<string, unknown>
		fingerprint: string[]
	}
}

export function collectErrorMessages(error: unknown, depth = 0): string {
	if (depth > 5 || error == null) return ""
	if (typeof error === "string") return error
	if (error instanceof Error) {
		const cause = (error as { cause?: unknown }).cause
		return `${error.message}\n${collectErrorMessages(cause, depth + 1)}`
	}
	try {
		return JSON.stringify(error)
	} catch {
		return String(error)
	}
}

// User-config or transient-upstream conditions, never code bugs: revoked or
// invalid integration tokens, suspended apps, upstream rate limits, and 5xx
// gateway blips the job retries. Per our policy these never reach Sentry.
const EXPECTED_INTEGRATION_ERROR_PATTERNS: RegExp[] = [
	/unauthorized/i,
	/suspended application/i,
	/access token is no longer valid/i,
	/authentication required/i,
	/not authenticated/i,
	/\btoken (?:has )?expired\b/i,
	/invalid[\w\s]*token/i,
	/token refresh failed/i,
	/failed to refresh[\w\s]*token/i,
	/could not be refreshed/i,
	/\binvalid_grant\b/i,
	/(?:http-code|status(?: code)?|code)[:\s]*40[13]\b/i,
	/(?:http-code|status(?: code)?|code)[:\s]*429\b/i,
	/(?:http-code|status(?: code)?|code)[:\s]*50[234]\b/i,
	/"status":"error","message":"internal error"/i,
	/rate limit(?:ed|s)? (?:exceeded|reached|hit)/i,
	/too many requests/i,
	/upstream connect error/i,
	/connection timeout/i,
	/service unavailable/i,
	/bad gateway/i,
	/gateway timeout/i,
	/econnreset/i,
	/etimedout/i,
	/socket hang up/i,
	// Prisma P2024: every pooled connection was busy for a moment, so the
	// request failed before it ran.
	/Timed out fetching a new connection from the connection pool/i,
	/no active subscription/i,
	/is not connected for this workspace/i,
	// The customer's Linear workspace has the Customers feature switched off.
	/customer requests are not available in this workspace/i,
	/customers feature not enabled/i,
	// The workspace still points at a Linear team that was retired in Linear.
	/Could not modify retired team/i,
	// Linear's own search reranker failing; link suggestions fall back to no hits.
	/Failed to rerank search results/i,
]

/**
 * True for integration failures we never report: revoked/invalid tokens,
 * suspended apps, upstream rate limits, and transient upstream 5xx. Inspects the cause chain.
 */
export function isExpectedIntegrationError(error: unknown): boolean {
	const message = collectErrorMessages(error)
	return EXPECTED_INTEGRATION_ERROR_PATTERNS.some((re) => re.test(message))
}

function toError(error: unknown): Error {
	if (error instanceof Error) {
		return error
	}
	if (typeof error === "string") {
		return new Error(error)
	}
	try {
		return new Error(JSON.stringify(error))
	} catch {
		return new Error(String(error))
	}
}

/**
 * Build a consistent Sentry event. The title is always "[domain] action: message"
 * so events are scannable and groupable in the Sentry issue list. workspaceId,
 * userId and an ISO timestamp are always present in the context.
 */
export function buildSentryEnvelope(
	error: unknown,
	context: ErrorContext,
	service?: ServiceName,
): SentryEnvelope {
	const original = toError(error)
	const baseMessage = original.message || "Unknown error"

	const titled = new Error(
		`[${context.domain}] ${context.action}: ${baseMessage}`,
	)
	titled.name = original.name === "Error" ? "ProductlaneError" : original.name
	titled.stack = original.stack
	// `cause` keeps Sentry grouping on the underlying failure; cast so this
	// compiles regardless of each consuming app's TS `lib` target.
	;(titled as { cause?: unknown }).cause = original

	const tags: Record<string, string> = {
		domain: context.domain,
		action: context.action,
	}
	if (service) {
		tags.service = service
	}
	if (context.workspaceId) {
		tags.workspaceId = context.workspaceId
	}
	if (context.userId) {
		tags.userId = context.userId
	}

	return {
		error: titled,
		captureContext: {
			tags,
			extra: {
				workspaceId: context.workspaceId ?? null,
				userId: context.userId ?? null,
				timestamp: new Date().toISOString(),
				...context.extra,
			},
			// Group by domain + action + base message so the same failure clusters into one issue.
			fingerprint: [context.domain, context.action, baseMessage],
		},
	}
}
